var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineStore } from "pinia";
import ComponentEndpoints from "src/shared/api/campaign/component/ComponentEndpoints";
import { toPromise } from "src/shared/api/promise";
import _ from "underscore";
import { reactive } from "vue";
import BuiltComponent from "./model/BuiltComponent";
import { useComponentGraphStore } from "src/shared/store/component-graph";
import { campaignComponentChangeEvent, campaignComponentDeletedEvent, campaignComponentListChangeEvent, } from "../event-bus/campaign-component";
import { createCampaignComponentIndex } from "../component/createCampaignComponentIndex";
import { campaignArchiveEvent } from "../event-bus/campaign";
import { sortBy } from "lodash";
import { builtComponentUpdatedEvent } from "../event-bus/component-built";
import { useComponentVariableSearchableStore } from "../component-variable-searchable";
import { useComponentGraphStoreV2 } from "../component-graph-v2";
import { fetchAll } from "src/shared/store/rest/fetch-all";
import { storeInboundEventBus } from "src/shared/data-loading/inbound/bus/storeInboundEventBus";
const fetchBuiltComponentsFunction = (data) => __awaiter(void 0, void 0, void 0, function* () {
    return yield toPromise(ComponentEndpoints.listBuilt(data));
});
const getStringifiedVariableStateThatCanAffectComponentGraph = (builtComponent) => {
    const sortedVariables = sortBy(builtComponent.listVariables(), "name");
    const sourceStates = sortedVariables.map((variable) => {
        return {
            name: variable.name,
            source_component_id: variable.source_component_id,
            source: variable.source,
        };
    });
    const comparableString = sourceStates
        .map((state) => {
        return `${state.name}:${state.source}:${state.source_component_id}`;
    })
        .join(",");
    return comparableString;
};
const doesBuiltComponentUpdateAffectComponentGraph = (oldComponent, newComponent) => {
    const hasComponentBeenAdded = !oldComponent;
    const haveComponentParentsChanged = ((oldComponent === null || oldComponent === void 0 ? void 0 : oldComponent.component_ids) || []).sort().join() !==
        newComponent.component_ids.sort().join();
    if (hasComponentBeenAdded || haveComponentParentsChanged) {
        return true;
    }
    const haveAnyVariablesBeenChangedThatAffectGraph = getStringifiedVariableStateThatCanAffectComponentGraph(oldComponent) !==
        getStringifiedVariableStateThatCanAffectComponentGraph(newComponent);
    return haveAnyVariablesBeenChangedThatAffectGraph;
};
export const useBuiltComponentStore = defineStore("builtComponentStore", () => {
    const componentGraphStore = useComponentGraphStore();
    const componentGraphStoreV2 = useComponentGraphStoreV2();
    const componentVariableSearchableStore = useComponentVariableSearchableStore();
    const state = reactive({
        builtComponents: {},
    });
    const campaignComponentIndex = createCampaignComponentIndex();
    const setComponents = (builtComponentResponses) => {
        const parentComponentIds = new Set();
        const output = _.map(builtComponentResponses, (response) => {
            const component = new BuiltComponent(response);
            const oldComponent = state.builtComponents[component.id];
            state.builtComponents[component.id] = component;
            campaignComponentIndex
                .get(component.campaign_id)
                .addComponentId(component.id);
            builtComponentUpdatedEvent.emit({
                componentId: component.id,
            });
            componentVariableSearchableStore.recalculateSearchables(component.id);
            if (doesBuiltComponentUpdateAffectComponentGraph(oldComponent, component)) {
                componentGraphStoreV2.updateComponentGraphNode(component);
            }
            component.component_ids.forEach((componentId) => {
                parentComponentIds.add(componentId);
            });
            componentGraphStore.addComponent(component);
            return component;
        });
        parentComponentIds.forEach((componentId) => {
            if (!state.builtComponents[componentId]) {
                fetchBuiltComponent(componentId);
            }
        });
        return output;
    };
    const fetchBuiltComponents = (data) => __awaiter(void 0, void 0, void 0, function* () {
        const hasNoLimitSpecified = !(data === null || data === void 0 ? void 0 : data.limit);
        if (hasNoLimitSpecified) {
            const allComponents = yield fetchAll(fetchBuiltComponentsFunction, data || {});
            setComponents(allComponents);
        }
        else {
            setComponents(yield toPromise(ComponentEndpoints.listBuilt(data)));
        }
    });
    const fetchBuiltComponentDuplicationCandidates = (targetComponentId) => __awaiter(void 0, void 0, void 0, function* () {
        return setComponents(yield toPromise(ComponentEndpoints.listBuilt({
            target_component_id: targetComponentId,
        })));
    });
    const fetchCampaignBuiltComponents = (campaignId) => __awaiter(void 0, void 0, void 0, function* () {
        const builtComponentResponses = yield toPromise(ComponentEndpoints.listBuilt({
            campaign_ids: campaignId,
        }));
        setComponents(builtComponentResponses);
    });
    const fetchBuiltComponent = (componentId) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield toPromise(ComponentEndpoints.getBuilt(componentId));
        setComponents([response]);
    });
    const getBuiltComponent = (id) => {
        return state.builtComponents[id];
    };
    const getBuiltComponents = () => {
        return _.values(state.builtComponents);
    };
    const getCampaignBuiltComponents = (campaignId) => {
        const index = campaignComponentIndex.get(campaignId);
        return _.map(index.getComponentIds(), (componentId) => {
            return state.builtComponents[componentId];
        });
    };
    campaignComponentChangeEvent.on((event) => {
        fetchBuiltComponent(event.componentId);
    });
    campaignComponentListChangeEvent.on((event) => {
        fetchCampaignBuiltComponents(event.campaignId);
    });
    campaignComponentDeletedEvent.on((payload) => {
        delete state.builtComponents[payload.componentId];
    });
    campaignArchiveEvent.on((payload) => {
        const components = getBuiltComponents();
        const deletedComponents = _.filter(components, (component) => {
            return component.campaign_id === payload.campaignId;
        });
        _.each(deletedComponents, (component) => {
            delete state.builtComponents[component.id];
        });
    });
    storeInboundEventBus.on("built_component_updated", (payload) => {
        setComponents([payload.builtComponent]);
    });
    return {
        state,
        fetchBuiltComponents,
        fetchBuiltComponentDuplicationCandidates,
        fetchBuiltComponent,
        getBuiltComponents,
        getCampaignBuiltComponents,
        getBuiltComponent,
    };
});
